/* Browns Digital Studio — credits & Browns Digital UI checkout */
"use strict";

const PLAN_ALIASES = { pro: "forge", studio: "architect" };

const CREDIT_PLANS = {
  free:      { label: "Draft",     monthlyCredits: 0,    paystackPlan: null },
  build:     { label: "Build",     monthlyCredits: 250,  paystackPlan: "build" },
  forge:     { label: "Forge",     monthlyCredits: 750,  paystackPlan: "forge" },
  architect: { label: "Architect", monthlyCredits: 2500, paystackPlan: "architect" }
};

const SUBSCRIPTION_PRICING = {
  build: {
    monthly: { kobo: 6900,   zar: 69,   per: "month" },
    annual:  { kobo: 68900,  zar: 689,  per: "year",  save: "Save 17%" }
  },
  forge: {
    monthly: { kobo: 14900,  zar: 149,  per: "month" },
    annual:  { kobo: 149900, zar: 1499, per: "year",  save: "Save 17%" }
  },
  architect: {
    monthly: { kobo: 39900,  zar: 399,  per: "month" },
    annual:  { kobo: 399900, zar: 3999, per: "year",  save: "Save 17%" }
  }
};

const SUBSCRIPTION_FEATURES = {
  build:     ["1 site", "Hosting included", "Full builder", "Custom CSS"],
  forge:     ["3 sites", "Hosting included", "Collaborators", "Custom CSS"],
  architect: ["Unlimited sites", "Hosting + custom domains", "Unlimited collaborators", "Priority support"]
};

const CREDIT_TOPUPS = [
  { id: "boost_100",  credits: 100,  amountZAR: 99,  label: "100 credits",  tag: "Quick boost" },
  { id: "boost_350",  credits: 350,  amountZAR: 299, label: "350 credits",  tag: "Popular" },
  { id: "boost_1000", credits: 1000, amountZAR: 749, label: "1,000 credits", tag: "Best value" }
];

function bdNormalizePlan(plan){
  const p = String(plan || "free").toLowerCase();
  return PLAN_ALIASES[p] || p;
}

function bdPlanCreditAllowance(plan){
  const key = bdNormalizePlan(plan);
  return CREDIT_PLANS[key]?.monthlyCredits ?? 0;
}

function bdPlanDisplayLabel(plan){
  const key = bdNormalizePlan(plan);
  const fromCredit = CREDIT_PLANS[key]?.label;
  if (fromCredit) return fromCredit;
  return (PLAN_RULES[plan] || PLAN_RULES.free).label;
}

function bdSubPricing(planKey, interval){
  const p = SUBSCRIPTION_PRICING[planKey];
  if (!p) return { kobo: 0, zar: 0, per: interval === "annual" ? "year" : "month" };
  return p[interval === "annual" ? "annual" : "monthly"] || p.monthly;
}

function bdCreditTopup(id){
  return CREDIT_TOPUPS.find(t => t.id === id) || null;
}

function bdFormatCredits(n){
  const v = Math.max(0, Math.floor(Number(n) || 0));
  return v.toLocaleString();
}

function bdCreditTotal(balance){
  if (!balance) return 0;
  return Math.max(0, (balance.plan_credits || 0) + (balance.topup_credits || 0));
}

/** True when the user still has a paid plan. A cancel date that has passed does not count. */
function bdHasActiveSubscription(balance){
  if (!balance) return false;
  const st = balance.subscription?.status;
  if (st === "non-renewing") {
    const ends = Date.parse(balance.subscription?.ends_at || "");
    if (Number.isFinite(ends) && ends <= Date.now()) return false;
    return true;
  }
  if (!!balance.has_subscription || st === "active" || st === "attention") return true;
  const tier = bdNormalizePlan(balance.plan);
  return ["build", "forge", "architect"].includes(tier) && st !== "none";
}

function bdFormatZAR(kobo){
  const v = Math.max(0, Number(kobo) || 0) / 100;
  return "R" + v.toLocaleString("en-ZA", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });
}

function bdFormatZARFromRands(rands){
  return "R" + Number(rands || 0).toLocaleString("en-ZA");
}

function bdListenPaystackComplete(onReference){
  function handler(ev){
    if (ev.origin !== location.origin) return;
    const data = ev.data;
    if (!data || data.type !== "bd-paystack-complete") return; /* brand-ok */
    window.removeEventListener("message", handler);
    if (data.reference) onReference(data.reference);
  }
  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

/** Browns Digital UI checkout sheet — payment embeds inside our UI, not a Paystack popup. */
function bdOpenCheckoutSheet(opts){
  const {
    title, subtitle, rows = [], totalLabel, totalDisplay,
    confirmLabel = "Pay now", onInit, onVerified, onDemo, onClose
  } = opts;

  document.getElementById("bd-checkout-sheet")?.remove();

  const root = document.createElement("div");
  root.id = "bd-checkout-sheet";
  root.className = "bd-checkout";
  root.innerHTML = `
    <div class="bd-checkout-backdrop" data-bd-close></div>
    <div class="bd-checkout-panel" role="dialog" aria-modal="true" aria-labelledby="bd-checkout-title">
      <header class="bd-checkout-head">
        <button type="button" class="bd-checkout-close" data-bd-close aria-label="Close">${bdCheckoutIcon("x")}</button>
        <p class="bd-checkout-kicker">Checkout</p>
        <h2 id="bd-checkout-title">${title}</h2>
        ${subtitle ? `<p class="bd-checkout-sub">${subtitle}</p>` : ""}
      </header>
      <div class="bd-checkout-body">
        <section class="bd-checkout-summary">
          <div class="bd-checkout-card">
            ${rows.map(r => `
              <div class="bd-checkout-row">
                <span>${r.label}</span>
                <strong>${r.value}</strong>
              </div>`).join("")}
            <div class="bd-checkout-total">
              <span>${totalLabel || "Total"}</span>
              <strong>${totalDisplay}</strong>
            </div>
          </div>
          <p class="bd-checkout-trust">${bdCheckoutIcon("lock")} Secure payment</p>
        </section>
        <section class="bd-checkout-pay" hidden>
          <p class="bd-checkout-pay-lead">Checkout will open. On Safari / iPhone you can use Apple Pay once the domain is verified.</p>
          <div class="bd-checkout-frame" id="bd-checkout-frame"></div>
          <p class="bd-checkout-pay-hint">Your payment is encrypted and processed securely.</p>
        </section>
        <section class="bd-checkout-done" hidden>
          <div class="bd-checkout-done-mark">${bdCheckoutIcon("check")}</div>
          <p class="bd-checkout-done-title">Payment confirmed</p>
          <p class="bd-checkout-done-sub" id="bd-checkout-done-sub"></p>
        </section>
      </div>
      <footer class="bd-checkout-foot">
        <button type="button" class="bd-checkout-primary" id="bd-checkout-confirm">${confirmLabel}</button>
      </footer>
    </div>`;

  document.body.appendChild(root);
  document.body.classList.add("bd-checkout-open");
  requestAnimationFrame(() => root.classList.add("show"));

  let closed = false;
  let paying = false;
  let pollTimer = null;

  function close(){
    if (closed) return;
    closed = true;
    clearInterval(pollTimer);
    root.classList.remove("show");
    document.body.classList.remove("bd-checkout-open");
    setTimeout(() => root.remove(), 280);
    onClose?.();
  }

  function showPayStep(){
    root.querySelector(".bd-checkout-summary").hidden = true;
    root.querySelector(".bd-checkout-pay").hidden = false;
    root.querySelector(".bd-checkout-foot").hidden = true;
  }

  function showSuccess(){
    if (closed) return;
    clearInterval(pollTimer);
    root.querySelector(".bd-checkout-summary").hidden = true;
    root.querySelector(".bd-checkout-pay").hidden = true;
    root.querySelector(".bd-checkout-foot").hidden = true;
    const sub = root.querySelector("#bd-checkout-done-sub");
    if (sub) sub.textContent = title || "";
    root.querySelector(".bd-checkout-done").hidden = false;
    root.classList.add("paid");
    setTimeout(close, 1400);
  }

  function startPoll(reference){
    clearInterval(pollTimer);
    let tries = 0;
    let doneUi = false;
    pollTimer = setInterval(async () => {
      tries++;
      if (tries > 60 || closed) { clearInterval(pollTimer); return; }
      try {
        const v = await onVerified(reference, { poll: true });
        if ((v?.ok || v?.balance) && !doneUi) {
          doneUi = true;
          clearInterval(pollTimer);
          showSuccess();
        }
      } catch { /* still pending */ }
    }, 2500);
  }

  root.querySelectorAll("[data-bd-close]").forEach(el => el.addEventListener("click", close));

  root.querySelector("#bd-checkout-confirm").addEventListener("click", async () => {
    if (paying) return;
    paying = true;
    const btn = root.querySelector("#bd-checkout-confirm");
    btn.disabled = true;
    btn.textContent = "Preparing…";
    try {
      const init = await onInit();
      if (init?.demo) {
        await onDemo?.(init);
        showSuccess();
        return;
      }
      showPayStep();
      const stopListen = bdListenPaystackComplete(reference => {
        clearInterval(pollTimer);
        stopListen();
        onVerified(reference, { poll: false }).then(() => showSuccess()).catch(() => {});
      });
      await bdEmbedPaystack(root.querySelector("#bd-checkout-frame"), init, reference => {
        stopListen();
        clearInterval(pollTimer);
        onVerified(reference, { poll: false }).then(() => showSuccess()).catch(() => {});
      }, close);
      startPoll(init.reference);
    } catch (e) {
      btn.disabled = false;
      btn.textContent = confirmLabel;
      paying = false;
      throw e;
    }
  });

  return { close };
}

function bdPaystackCheckoutUrl(init){
  const code = String(init?.access_code || "").trim();
  let url = String(init?.authorization_url || "").trim();
  if (url) url = url.replace(/\/popup\/?/gi, "/");
  else if (code) url = "https://checkout.paystack.com/" + encodeURIComponent(code); /* brand-ok */
  return url;
}

function bdLoadPaystackInline(){
  if (typeof PaystackPop !== "undefined") return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-bd-paystack-inline]'); /* brand-ok */
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Could not load checkout.")));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://js.paystack.co/v2/inline.js"; /* brand-ok */
    s.async = true;
    s.dataset.bdPaystackInline = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load checkout."));
    document.head.appendChild(s);
  });
}

function bdOpenPaystackIframeSheet({ title, subtitle, url }){
  if (!url) throw new Error("Could not open secure page.");
  document.getElementById("bd-checkout-sheet")?.remove();
  const root = document.createElement("div");
  root.id = "bd-checkout-sheet";
  root.className = "bd-checkout";
  root.innerHTML = `
    <div class="bd-checkout-backdrop" data-bd-close></div>
    <div class="bd-checkout-panel" role="dialog" aria-modal="true">
      <header class="bd-checkout-head">
        <button type="button" class="bd-checkout-close" data-bd-close aria-label="Close">${bdCheckoutIcon("x")}</button>
        <p class="bd-checkout-kicker">Secure</p>
        <h2>${title}</h2>
        ${subtitle ? `<p class="bd-checkout-sub">${subtitle}</p>` : ""}
      </header>
      <div class="bd-checkout-body bd-checkout-body--frame">
        <div class="bd-checkout-frame" id="bd-checkout-frame"></div>
      </div>
    </div>`;
  document.body.appendChild(root);
  document.body.classList.add("bd-checkout-open");
  requestAnimationFrame(() => root.classList.add("show"));
  let closed = false;
  function close(){
    if (closed) return;
    closed = true;
    root.classList.remove("show");
    document.body.classList.remove("bd-checkout-open");
    setTimeout(() => root.remove(), 280);
  }
  root.querySelectorAll("[data-bd-close]").forEach(el => el.addEventListener("click", close));
  const frame = root.querySelector("#bd-checkout-frame");
  const iframe = document.createElement("iframe");
  iframe.src = url;
  iframe.title = title;
  iframe.className = "bd-checkout-iframe";
  iframe.setAttribute("allow", "payment *");
  iframe.referrerPolicy = "strict-origin-when-cross-origin";
  frame.appendChild(iframe);
  return { close };
}

function bdEmbedPaystackIframe(container, checkoutUrl){
  container.innerHTML = "";
  const iframe = document.createElement("iframe");
  iframe.src = checkoutUrl;
  iframe.title = "Secure payment";
  iframe.className = "bd-checkout-iframe";
  iframe.setAttribute("allow", "payment *");
  iframe.referrerPolicy = "strict-origin-when-cross-origin";
  container.appendChild(iframe);
}

/**
 * Paystack InlineJS v2 — required for Apple Pay on Safari / iOS.
 * Falls back to hosted checkout iframe when InlineJS is unavailable.
 */
async function bdEmbedPaystack(container, init, onSuccess, onClose){
  const amount = Math.max(0, Number(init.amount) || 0);
  if (!amount) throw new Error("Could not determine payment amount.");
  const accessCode = String(init?.access_code || "").trim();
  const publicKey = String(init?.public_key || (typeof PAYSTACK_PUBLIC_KEY !== "undefined" ? PAYSTACK_PUBLIC_KEY : "")).trim();
  const checkoutUrl = bdPaystackCheckoutUrl(init);

  try {
    await bdLoadPaystackInline();
    if (typeof PaystackPop === "undefined") throw new Error("Checkout could not start.");
    const pop = new PaystackPop();
    container.innerHTML = `<p class="bd-checkout-pay-lead">Complete payment in the checkout window. On Safari / iPhone, Apple Pay appears when enabled for this domain.</p>`;

    const handlers = {
      onSuccess: (tranx) => {
        const ref = String(tranx?.reference || init?.reference || "").trim();
        if (ref) onSuccess?.(ref);
      },
      onCancel: () => { onClose?.(); },
      onError: () => { onClose?.(); }
    };

    if (accessCode && typeof pop.resumeTransaction === "function") {
      pop.resumeTransaction(accessCode, handlers);
      return;
    }
    if (typeof pop.checkout === "function" && publicKey) {
      await pop.checkout({
        key: publicKey,
        email: String(init.email || ""),
        amount,
        currency: "ZAR",
        ref: String(init.reference || ""),
        ...handlers
      });
      return;
    }
    throw new Error("Inline checkout unavailable");
  } catch {
    if (!checkoutUrl) throw new Error("Could not load secure checkout. Please try again.");
    bdEmbedPaystackIframe(container, checkoutUrl);
  }
}

function bdCheckoutIcon(name){
  const icons = {
    x: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    check: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>',
    lock: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>'
  };
  return icons[name] || "";
}
